// Task #3972 — Browser push (Web Push / VAPID) subscription hook.
//
// Powers the "Enable on this device" affordance in My Profile → Notifications.
// Registers `/push-sw.js`, asks for notification permission, subscribes via the
// PushManager with the server's VAPID public key, and persists the subscription
// so assignment pushes can be delivered. Subscriptions are per browser/device.
import { useCallback, useEffect, useState } from "react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { isInsideNativeApp } from "@/lib/onesignal-native";

const SW_URL = "/push-sw.js";
const SW_SCOPE = "/";

// VAPID public keys are base64url; PushManager wants a Uint8Array.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export type PushSubscriptionState = {
  /** This browser exposes the service-worker + push + notification APIs. */
  supported: boolean;
  /** Server has VAPID keys (null while still loading). */
  configured: boolean | null;
  permission: NotificationPermission | "unsupported";
  /** A live push subscription exists for this browser. */
  subscribed: boolean;
  busy: boolean;
  error: string | null;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
};

function isSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    !isInsideNativeApp() &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function usePushSubscription(): PushSubscriptionState {
  const supported = isSupported();
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    supported ? Notification.permission : "unsupported",
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On mount: learn whether the server is configured and whether this browser
  // already holds a subscription.
  useEffect(() => {
    if (!supported) {
      setConfigured(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiRequest("GET", "/api/app/push/vapid-public-key");
        const body = (await res.json()) as { configured?: boolean };
        if (!cancelled) setConfigured(!!body.configured);
      } catch {
        if (!cancelled) setConfigured(false);
      }
      try {
        const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
        const existing = reg ? await reg.pushManager.getSubscription() : null;
        if (!cancelled) setSubscribed(!!existing);
      } catch {
        /* getRegistration can reject in some browsers — treat as unsubscribed */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const enable = useCallback(async () => {
    // The wrapper owns native push. Never create/re-register a browser VAPID
    // subscription there, even if its WebView exposes browser push APIs.
    if (isInsideNativeApp()) {
      setError("Browser push is unavailable inside the native app.");
      return;
    }
    if (!supported) {
      setError("This browser does not support push notifications.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const keyRes = await apiRequest("GET", "/api/app/push/vapid-public-key");
      const keyBody = (await keyRes.json()) as {
        publicKey?: string | null;
        configured?: boolean;
      };
      setConfigured(!!keyBody.configured);
      if (!keyBody.publicKey) {
        setError("Push notifications are not configured on this server yet.");
        return;
      }

      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setError("Notification permission was not granted.");
        return;
      }

      const reg = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
      await navigator.serviceWorker.ready;

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyBody.publicKey),
        });
      }

      const json = sub.toJSON();
      await apiRequest("POST", "/api/app/push/subscribe", {
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? "",
        auth: json.keys?.auth ?? "",
        userAgent: navigator.userAgent,
      });
      setSubscribed(true);
    } catch (err) {
      setError(parseErrorMessage(err, "Failed to enable push notifications."));
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported || isInsideNativeApp()) return;
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await apiRequest("POST", "/api/app/push/unsubscribe", {
          endpoint: sub.endpoint,
        });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      setError(parseErrorMessage(err, "Failed to disable push notifications."));
    } finally {
      setBusy(false);
    }
  }, [supported]);

  return { supported, configured, permission, subscribed, busy, error, enable, disable };
}
